import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import axios from 'axios'
import { toast } from 'react-toastify'
import { FaCoins, FaCheckCircle } from 'react-icons/fa'
import { getCreditPricing, getProPricing } from '../../utils/pricingConfig'
import PaymentGateway, { PaymentGatewayRef } from '../payment/PaymentGateway'
import {
  connectWebSocket,
  listenToEvent,
  disconnectWebSocket,
} from '../payment/webSocketService'

interface IpInfoResponse {
  ip: string
  country: string
  city?: string
  region?: string
}

interface PaymentData {
  [key: string]: any
}

interface SubscriptionData {
  [key: string]: any
}

const Pricing: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  
  // Get orgId and userId from URL params
  const urlOrgId = searchParams.get('orgId')
  const urlUserId = searchParams.get('userId')
  
  // State
  const [currency, setCurrency] = useState<'INR' | 'USD'>('USD')
  const [isLoading, setIsLoading] = useState(true)
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [paymentType, setPaymentType] = useState<'credits' | 'pro' | 'lifetime'>('credits')
  const [creditsAvailable, setCreditsAvailable] = useState(0)
  const [creditsToBuy, setCreditsToBuy] = useState(10)
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly')
  const [authToken, setAuthToken] = useState<string>('')
  const [orgId, setOrgId] = useState<string>('')
  const [userId, setUserId] = useState<string>('')
  const [monthlyPlan, setMonthlyPlan] = useState<any>(null)
  const [yearlyPlan, setYearlyPlan] = useState<any>(null)
  const [userPlan, setUserPlan] = useState<string>('free')
  
  const paymentGatewayRef = useRef<PaymentGatewayRef>(null)
  
  // Pricing based on currency
  const creditValue = getCreditPricing(currency)
  const proPricing = getProPricing(currency)
  const currencySymbol = currency === 'INR' ? '₹' : '$'

  // Detect currency and set org info on mount
  useEffect(() => {
    const initializePage = async () => {
      setIsLoading(true)
      
      // Set orgId and userId from URL or sessionStorage
      const finalOrgId = urlOrgId || sessionStorage.getItem('orgId') || ''
      const finalUserId = urlUserId ? decodeURIComponent(urlUserId) : sessionStorage.getItem('userId') || ''
      const token = sessionStorage.getItem('authToken') || ''
      
      setOrgId(finalOrgId)
      setUserId(finalUserId)
      setAuthToken(token)
      
      // Store in session if from URL
      if (urlOrgId) {
        sessionStorage.setItem('orgId', urlOrgId)
      }
      if (urlUserId) {
        sessionStorage.setItem('userId', decodeURIComponent(urlUserId))
      }
      
      try {
        // Detect currency from IP
        const ipInfoResponse = await fetch(
          'https://api.zynth.ai/api/v1/data/userprofile/ip-info'
        )
        const ipInfoData: IpInfoResponse = await ipInfoResponse.json()
        const detectedCurrency = ipInfoData.country === 'IN' ? 'INR' : 'USD'
        setCurrency(detectedCurrency)
        sessionStorage.setItem('currency', detectedCurrency)
        
        // Fetch pricing plans
        if (token) {
          const planRes = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/v1/data/appscripts/razorpay/plans`,
            { headers: { Authorization: `Bearer ${token}` } }
          )
          const plans = planRes.data.items
          
          if (detectedCurrency === 'INR') {
            setMonthlyPlan(plans[5])
            setYearlyPlan(plans[3])
          } else {
            setMonthlyPlan(plans[4])
            setYearlyPlan(plans[2])
          }
          
          // Fetch org profile for credits and plan
          if (finalOrgId) {
            const orgRes = await axios.get(
              `${process.env.REACT_APP_BACKEND_URL}/api/v1/data/organizationprofile/organization/${finalOrgId}`,
              { headers: { Authorization: `Bearer ${token}` } }
            )
            setCreditsAvailable(orgRes.data?.credits || 0)
            setUserPlan(orgRes.data?.plan?.plan_name || 'free')
          }
        }
      } catch (error) {
        console.error('Error initializing pricing page:', error)
        setCurrency('USD')
      } finally {
        setIsLoading(false)
      }
    }
    
    initializePage()
  }, [urlOrgId, urlUserId])

  // WebSocket connection for payment events
  useEffect(() => {
    if (!orgId) return
    
    connectWebSocket(orgId)
    
    listenToEvent('payment.authorized', (data: PaymentData) => {
      console.log('Payment authorized:', data)
      toast.success('Payment successful!')
      refreshCredits()
    })
    
    listenToEvent('subscription.authenticated', (data: SubscriptionData) => {
      console.log('Subscription authenticated:', data)
      toast.success('Subscription activated!')
      window.location.reload()
    })
    
    return () => {
      disconnectWebSocket()
    }
  }, [orgId])

  // Refresh credits
  const refreshCredits = async () => {
    if (!orgId || !authToken) return
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/data/organizationprofile/organization/${orgId}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      )
      setCreditsAvailable(res.data?.credits || 0)
      setUserPlan(res.data?.plan?.plan_name || 'free')
      toast.success('Credits refreshed!')
    } catch (err) {
      console.error('Failed to refresh credits:', err)
    }
  }

  // Handle Buy Credits
  const handleBuyCredits = () => {
    if (!orgId || !authToken) {
      // Save where to come back and what to do after auth
      try {
        sessionStorage.setItem('redirectAfterAuth', window.location.pathname + window.location.search)
        sessionStorage.setItem('postAuthAction', JSON.stringify({ action: 'buyCredits', creditsToBuy }))
      } catch (err) {
        console.warn('Failed to store post-auth action', err)
      }
      navigate('/auth')
      return
    }
    setPaymentType('credits')
    setShowPaymentModal(true)
  }

  // If user was redirected to login to buy credits, replay the action after auth
  useEffect(() => {
    if (isLoading) return
    if (!authToken || !orgId) return

    const raw = sessionStorage.getItem('postAuthAction')
    if (!raw) return

    let parsed: any = null
    try {
      parsed = JSON.parse(raw)
    } catch (e) {
      sessionStorage.removeItem('postAuthAction')
      return
    }

    if (parsed?.action === 'buyCredits') {
      // restore credits selection if provided
      if (typeof parsed.creditsToBuy === 'number') {
        setCreditsToBuy(parsed.creditsToBuy)
      }

      setPaymentType('credits')
      setShowPaymentModal(true)

      // wait for PaymentGateway ref to be ready then trigger payment
      let attempts = 0
      const maxAttempts = 20
      const interval = setInterval(() => {
        attempts += 1
        if (paymentGatewayRef.current) {
          try {
            const count = parsed.creditsToBuy || creditsToBuy
            paymentGatewayRef.current.triggerPayment({
              amount: parseFloat((count * creditValue).toFixed(2)),
              creditCount: count,
            })
          } catch (err) {
            console.error('Failed to trigger payment after auth replay', err)
          }
          clearInterval(interval)
          sessionStorage.removeItem('postAuthAction')
          sessionStorage.removeItem('redirectAfterAuth')
        } else if (attempts >= maxAttempts) {
          clearInterval(interval)
          // give up — we'll leave the modal open for user to click
        }
      }, 300)
    }
  }, [isLoading, authToken, orgId, creditsToBuy, creditValue])

  // Handle Pro subscription
  const handleStartPro = async () => {
    if (!orgId || !authToken) {
      navigate('/auth')
      return
    }
    
    if (!monthlyPlan || !yearlyPlan) {
      toast.error('Plans not loaded. Please refresh.')
      return
    }
    
    setIsLoading(true)
    const planID = billingCycle === 'annual' ? yearlyPlan.id : monthlyPlan.id
    const startAtUnix = Math.floor((Date.now() + 10 * 60 * 1000) / 1000)
    
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/data/payments/razorpay/create-subscription`,
        {
          plan_id: planID,
          customer_id: orgId,
          total_count: 12,
          start_at: startAtUnix,
          quantity: 1,
          orgId,
          userId,
          notes: { OrgId: orgId },
        },
        { headers: { Authorization: `Bearer ${authToken}` } }
      )
      
      const result = response.data
      if (result.short_url) {
        window.open(result.short_url, '_blank')
      } else {
        toast.error('Something went wrong. Please try again.')
      }
    } catch (error: any) {
      console.error('Subscription failed:', error)
      toast.error(`Payment failed: ${error?.message || 'Unknown error'}`)
    } finally {
      setIsLoading(false)
    }
  }

  // Handle Lifetime purchase
  const handleBuyLifetime = async () => {
    if (!orgId || !authToken) {
      navigate('/auth')
      return
    }
    
    const lifetimeAmount = currency === 'INR' ? 7999 : 99
    
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/data/payments/create-order`,
        {
          amount: lifetimeAmount,
          currency,
        },
        { headers: { Authorization: `Bearer ${authToken}` } }
      )
      
      const { id: order_id, amount, currency: orderCurrency } = response.data
      
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount,
        currency: orderCurrency,
        name: 'Zynth',
        description: 'Lifetime Access',
        order_id,
        handler: async function (response: any) {
          try {
            const verifyRes = await axios.post(
              `${process.env.REACT_APP_BACKEND_URL}/api/v1/data/payments/verify-payment`,
              {
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature,
                amount: lifetimeAmount,
              },
              { headers: { Authorization: `Bearer ${authToken}` } }
            )
            
            if (verifyRes.data) {
              // Update org profile with lifetime plan
              await axios.patch(
                `${process.env.REACT_APP_BACKEND_URL}/api/v1/data/organizationprofile/organizationedit/${orgId}`,
                { plan: { plan_name: 'lifetime' } },
                { headers: { Authorization: `Bearer ${authToken}` } }
              )
              toast.success('Lifetime access activated!')
              window.location.reload()
            }
          } catch (err) {
            console.error('Payment verification failed:', err)
            toast.error('Payment verification failed')
          }
        },
        prefill: {
          name: sessionStorage.getItem('userName') || 'Customer',
          email: sessionStorage.getItem('userEmail') || '',
        },
        theme: { color: '#3667B2' },
      }
      
      const rzp = new (window as any).Razorpay(options)
      rzp.open()
    } catch (error) {
      console.error('Lifetime purchase failed:', error)
      toast.error('Failed to initiate payment')
    }
  }

  // Trigger credit payment
  const handleCreditPayment = () => {
    if (paymentGatewayRef.current) {
      paymentGatewayRef.current.triggerPayment({
        amount: parseFloat((creditsToBuy * creditValue).toFixed(2)),
        creditCount: creditsToBuy,
      })
    }
  }

  // Payment success handler
  const handlePaymentSuccess = async (result: any) => {
    toast.success('Payment successful!')
    
    // Update credits in org profile
    if (orgId && authToken) {
      try {
        const newCredits = creditsAvailable + creditsToBuy
        await axios.patch(
          `${process.env.REACT_APP_BACKEND_URL}/api/v1/data/organizationprofile/organizationedit/${orgId}`,
          { credits: newCredits },
          { headers: { Authorization: `Bearer ${authToken}` } }
        )
        setCreditsAvailable(newCredits)
        setShowPaymentModal(false)
        toast.success(`${creditsToBuy} credits added to your account!`)
        // notify other tabs that credits changed
        try {
          localStorage.setItem('zynth:credits_updated', String(Date.now()))
        } catch (err) {
          console.warn('Failed to write credits_updated to localStorage', err)
        }
      } catch (err) {
        console.error('Failed to update credits:', err)
      }
    }
  }

  return (
    <div className="bg-gradient-to-br from-gray-50 via-blue-50 to-gray-50 w-full lg:p-12 p-4 min-h-screen" id="pricing">
      <section className="max-w-7xl mx-auto text-center py-12">
        <div className="inline-block px-4 py-2 bg-blue-100 rounded-full text-[#3667B2] text-sm font-semibold mb-4">
          💎 Transparent Pricing
        </div>
        <h1 className="text-4xl lg:text-5xl font-extrabold text-gray-900 mb-4">
          Pricing that scales with your needs
        </h1>
        <p className="text-lg text-gray-600 mt-4 max-w-2xl mx-auto">
          Pay-as-you-go credits for occasional users, or pick Pro / Lifetime for unlimited access to all Zynth tools.
        </p>
        
        {/* Show user info only when logged in (authToken present) and orgId available */}
        {orgId && authToken && (
          <div className="mt-4 flex items-center justify-center gap-4">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 rounded-full text-green-700 text-sm font-medium">
              <FaCoins className="text-yellow-500" />
              Credits: {creditsAvailable}
            </span>
            <button
              onClick={refreshCredits}
              className="text-sm text-blue-600 hover:underline"
            >
              ↻ Refresh
            </button>
          </div>
        )}
      </section>

      {/* Comprehensive Pricing Table */}
      <section className="max-w-7xl mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-200 mt-4">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            {/* Header Row with Plan Names */}
            <thead>
              <tr className="bg-gradient-to-r from-gray-50 via-blue-50 to-gray-50">
                <th className="py-6 px-6 text-left font-semibold text-gray-800"></th>
                <th className="py-6 px-6 text-center">
                  <div className="font-bold text-xl text-gray-800">Pay as you go</div>
                  <div className="text-xs text-gray-500 mt-1">Flexible usage</div>
                </th>
                <th className="pt-10 pb-6 px-6 text-center bg-gradient-to-br from-[#3667B2] to-[#2d5291] relative">
                  <div className="absolute top-2 left-1/2 transform -translate-x-1/2">
                    <span className="bg-yellow-400 text-yellow-900 text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                      ⭐ POPULAR
                    </span>
                  </div>
                  <div className="font-bold text-xl text-white">Pro</div>
                  <div className="text-xs text-blue-100 mt-1">Best value</div>
                </th>
                <th className="py-6 px-6 text-center">
                  <div className="font-bold text-xl text-gray-800">Lifetime Access</div>
                  <div className="text-xs text-gray-500 mt-1">One-time investment</div>
                </th>
              </tr>
            </thead>

            <tbody>
              {/* Plan Features */}
              <tr className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                <td className="py-4 px-6 font-medium text-gray-700"></td>
                <td className="py-4 px-6 text-center text-gray-600 text-sm">No subscription. No monthly commitment.</td>
                <td className="py-4 px-6 text-center text-white text-sm bg-gradient-to-br from-[#3667B2] to-[#2d5291]">Unlimited access to all Zynth tools</td>
                <td className="py-4 px-6 text-center text-gray-600 text-sm">Unlimited access to all Zynth tools</td>
              </tr>
              <tr className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                <td className="py-4 px-6 font-medium text-gray-700"></td>
                <td className="py-4 px-6 text-center text-gray-600 text-sm">Pay for what you use</td>
                <td className="py-4 px-6 text-center text-white text-sm bg-gradient-to-br from-[#3667B2] to-[#2d5291]">Monthly subscription</td>
                <td className="py-4 px-6 text-center text-gray-600 text-sm">One-time payment</td>
              </tr>
              <tr className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                <td className="py-4 px-6 font-medium text-gray-700"></td>
                <td className="py-4 px-6 text-center text-gray-600 text-sm">Credits never expire</td>
                <td className="py-4 px-6 text-center text-white text-sm bg-gradient-to-br from-[#3667B2] to-[#2d5291]">Priority processing</td>
                <td className="py-4 px-6 text-center text-gray-600 text-sm">Priority processing</td>
              </tr>
              <tr className="border-b-2 border-gray-200 hover:bg-gray-50 transition-colors">
                <td className="py-4 px-6 font-medium text-gray-700"></td>
                <td className="py-4 px-6 text-center text-gray-600 text-sm">Credits work across all Zynth tools</td>
                <td className="py-4 px-6 text-center text-white text-sm bg-gradient-to-br from-[#3667B2] to-[#2d5291]">Priority support</td>
                <td className="py-4 px-6 text-center text-gray-600 text-sm">Priority support</td>
              </tr>

              {/* Pricing Row */}
              <tr className="bg-gradient-to-r from-blue-50 via-indigo-50 to-blue-50 border-b-2 border-gray-200">
                <td className="py-6 px-6 font-bold text-gray-800 text-base">💰 Pricing</td>
                <td className="py-6 px-6 text-center">
                  <div className="text-2xl font-extrabold text-gray-900">
                    {currencySymbol}{currency === 'INR' ? '199' : '2.5'}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">per 10 credits</div>
                </td>
                <td className="py-6 px-6 text-center bg-gradient-to-br from-[#3667B2] to-[#2d5291]">
                  <div className="text-2xl font-extrabold text-white">
                    {currencySymbol}{currency === 'INR' ? '749' : '9'}
                  </div>
                  <div className="text-sm text-blue-100 mt-1">per month</div>
                </td>
                <td className="py-6 px-6 text-center">
                  <div className="text-2xl font-extrabold text-gray-900">
                    {currencySymbol}{currency === 'INR' ? '7999' : '99'}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">one-time</div>
                </td>
              </tr>

              {/* CTA Buttons Row */}
              <tr className="border-b-2 border-gray-300 bg-white">
                <td className="py-6 px-6"></td>
                <td className="py-6 px-6 text-center">
                  <button 
                    onClick={handleBuyCredits}
                    disabled={isLoading}
                    className="px-8 py-3 border-2 border-[#3667B2] text-[#3667B2] rounded-xl hover:bg-[#3667B2] hover:text-white hover:shadow-lg hover:scale-105 transition-all duration-300 font-semibold disabled:opacity-50"
                  >
                    Buy Credits
                  </button>
                </td>
                <td className="py-6 px-6 text-center bg-gradient-to-br from-[#3667B2] to-[#2d5291]">
                  <button 
                    onClick={handleStartPro}
                    disabled={isLoading}
                    className="px-8 py-3 bg-white text-[#3667B2] rounded-xl hover:bg-yellow-400 hover:text-gray-900 hover:shadow-2xl hover:scale-105 transition-all duration-300 font-bold disabled:opacity-50"
                  >
                    {userPlan === 'pro' ? 'Current Plan' : 'Start Pro →'}
                  </button>
                </td>
                <td className="py-6 px-6 text-center">
                  <button 
                    onClick={handleBuyLifetime}
                    disabled={isLoading}
                    className="px-8 py-3 border-2 border-[#3667B2] text-[#3667B2] rounded-xl hover:bg-[#3667B2] hover:text-white hover:shadow-lg hover:scale-105 transition-all duration-300 font-semibold disabled:opacity-50"
                  >
                    {userPlan === 'lifetime' ? 'Current Plan' : 'Buy Lifetime'}
                  </button>
                </td>
              </tr>

              {/* Presentations Section */}
              <tr className="bg-gradient-to-r from-blue-100 via-indigo-100 to-blue-100">
                <td colSpan={4} className="py-4 px-6 font-bold text-gray-800 text-base">
                  📊 Presentations (Web App + Google Slides Add-on + Slack Agent)
                </td>
              </tr>
              <tr className="border-b border-gray-100 hover:bg-blue-50 transition-colors">
                <td className="py-4 px-6 text-gray-700 font-medium">Generate Presentation</td>
                <td className="py-4 px-6 text-center text-gray-600">10 / presentation</td>
                <td className="py-4 px-6 text-center text-white bg-gradient-to-br from-[#3667B2] to-[#2d5291] font-semibold">Unlimited</td>
                <td className="py-4 px-6 text-center text-gray-600">Unlimited</td>
              </tr>
              <tr className="border-b border-gray-100 hover:bg-blue-50 transition-colors">
                <td className="py-4 px-6 text-gray-700 font-medium">Generate Slide</td>
                <td className="py-4 px-6 text-center text-gray-600">5 / slide</td>
                <td className="py-4 px-6 text-center text-white bg-gradient-to-br from-[#3667B2] to-[#2d5291] font-semibold">Unlimited</td>
                <td className="py-4 px-6 text-center text-gray-600">Unlimited</td>
              </tr>
              <tr className="border-b border-gray-100 hover:bg-blue-50 transition-colors">
                <td className="py-4 px-6 text-gray-700 font-medium">AI Text Tools</td>
                <td className="py-4 px-6 text-center text-gray-600">2 / task</td>
                <td className="py-4 px-6 text-center text-white bg-gradient-to-br from-[#3667B2] to-[#2d5291] font-semibold">Unlimited</td>
                <td className="py-4 px-6 text-center text-gray-600">Unlimited</td>
              </tr>
              <tr className="border-b border-gray-100 hover:bg-blue-50 transition-colors">
                <td className="py-4 px-6 text-gray-700 font-medium">AI Image Search & Generation</td>
                <td className="py-4 px-6 text-center text-gray-600">2 / image</td>
                <td className="py-4 px-6 text-center text-white bg-gradient-to-br from-[#3667B2] to-[#2d5291] font-semibold">Unlimited</td>
                <td className="py-4 px-6 text-center text-gray-600">Unlimited</td>
              </tr>
              <tr className="border-b-2 border-gray-300 hover:bg-blue-50 transition-colors">
                <td className="py-4 px-6 text-gray-700 font-medium">Export (PPT/Google Slides)</td>
                <td className="py-4 px-6 text-center text-green-600 font-semibold">Free</td>
                <td className="py-4 px-6 text-center text-white bg-gradient-to-br from-[#3667B2] to-[#2d5291] font-semibold">Unlimited</td>
                <td className="py-4 px-6 text-center text-gray-600">Unlimited</td>
              </tr>

              {/* Document Builder Section */}
              <tr className="bg-gradient-to-r from-purple-100 via-pink-100 to-purple-100">
                <td colSpan={4} className="py-4 px-6 font-bold text-gray-800 text-base">
                  📄 Document Builder
                </td>
              </tr>
              <tr className="border-b border-gray-100 hover:bg-purple-50 transition-colors">
                <td className="py-4 px-6 text-gray-700 font-medium">Generate Document</td>
                <td className="py-4 px-6 text-center text-gray-600">10 / document</td>
                <td className="py-4 px-6 text-center text-white bg-gradient-to-br from-[#3667B2] to-[#2d5291] font-semibold">Unlimited</td>
                <td className="py-4 px-6 text-center text-gray-600">Unlimited</td>
              </tr>
              <tr className="border-b border-gray-100 hover:bg-purple-50 transition-colors">
                <td className="py-4 px-6 text-gray-700 font-medium">Generate Section</td>
                <td className="py-4 px-6 text-center text-gray-600">5 / section</td>
                <td className="py-4 px-6 text-center text-white bg-gradient-to-br from-[#3667B2] to-[#2d5291] font-semibold">Unlimited</td>
                <td className="py-4 px-6 text-center text-gray-600">Unlimited</td>
              </tr>
              <tr className="border-b border-gray-100 hover:bg-purple-50 transition-colors">
                <td className="py-4 px-6 text-gray-700 font-medium">AI Text Tools</td>
                <td className="py-4 px-6 text-center text-gray-600">2 / task</td>
                <td className="py-4 px-6 text-center text-white bg-gradient-to-br from-[#3667B2] to-[#2d5291] font-semibold">Unlimited</td>
                <td className="py-4 px-6 text-center text-gray-600">Unlimited</td>
              </tr>
              <tr className="border-b border-gray-100 hover:bg-purple-50 transition-colors">
                <td className="py-4 px-6 text-gray-700 font-medium">AI Image Search & Generation</td>
                <td className="py-4 px-6 text-center text-gray-600">2 / image</td>
                <td className="py-4 px-6 text-center text-white bg-gradient-to-br from-[#3667B2] to-[#2d5291] font-semibold">Unlimited</td>
                <td className="py-4 px-6 text-center text-gray-600">Unlimited</td>
              </tr>
              <tr className="border-b-2 border-gray-300 hover:bg-purple-50 transition-colors">
                <td className="py-4 px-6 text-gray-700 font-medium">Export (Doc/PDF)</td>
                <td className="py-4 px-6 text-center text-green-600 font-semibold">Free</td>
                <td className="py-4 px-6 text-center text-white bg-gradient-to-br from-[#3667B2] to-[#2d5291] font-semibold">Unlimited</td>
                <td className="py-4 px-6 text-center text-gray-600">Unlimited</td>
              </tr>

              {/* Social Media Agent Section */}
              <tr className="bg-gradient-to-r from-green-100 via-teal-100 to-green-100">
                <td colSpan={4} className="py-4 px-6 font-bold text-gray-800 text-base">
                  📱 Social Media Agent (Web App + Slack Agent)
                </td>
              </tr>
              <tr className="border-b border-gray-100 bg-gray-50">
                <td className="py-3 px-6 text-gray-500 italic text-sm"></td>
                <td className="py-3 px-6 text-center text-amber-600 italic font-medium text-sm">🚀 coming soon</td>
                <td className="py-3 px-6 text-center text-white italic font-medium text-sm bg-gradient-to-br from-[#3667B2] to-[#2d5291]">🚀 coming soon</td>
                <td className="py-3 px-6 text-center text-amber-600 italic font-medium text-sm">🚀 coming soon</td>
              </tr>
              <tr className="border-b border-gray-100 hover:bg-green-50 transition-colors">
                <td className="py-4 px-6 text-gray-700 font-medium">Text Posts</td>
                <td className="py-4 px-6 text-center text-gray-600">1 / post</td>
                <td className="py-4 px-6 text-center text-white bg-gradient-to-br from-[#3667B2] to-[#2d5291] font-semibold">Unlimited</td>
                <td className="py-4 px-6 text-center text-gray-600">Unlimited</td>
              </tr>
              <tr className="border-b border-gray-100 hover:bg-green-50 transition-colors">
                <td className="py-4 px-6 text-gray-700 font-medium">Image Posts</td>
                <td className="py-4 px-6 text-center text-gray-600">2 / post</td>
                <td className="py-4 px-6 text-center text-white bg-gradient-to-br from-[#3667B2] to-[#2d5291] font-semibold">Unlimited</td>
                <td className="py-4 px-6 text-center text-gray-600">Unlimited</td>
              </tr>
              <tr className="border-b border-gray-100 hover:bg-green-50 transition-colors">
                <td className="py-4 px-6 text-gray-700 font-medium">Video Posts</td>
                <td className="py-4 px-6 text-center text-gray-600">4 / post</td>
                <td className="py-4 px-6 text-center text-white bg-gradient-to-br from-[#3667B2] to-[#2d5291] font-semibold">Unlimited</td>
                <td className="py-4 px-6 text-center text-gray-600">Unlimited</td>
              </tr>
              <tr className="border-b-2 border-gray-300 hover:bg-green-50 transition-colors">
                <td className="py-4 px-6 text-gray-700 font-medium">Scheduling</td>
                <td className="py-4 px-6 text-center text-gray-600">1 / post / channel</td>
                <td className="py-4 px-6 text-center text-white bg-gradient-to-br from-[#3667B2] to-[#2d5291] font-semibold">Unlimited</td>
                <td className="py-4 px-6 text-center text-gray-600">Unlimited</td>
              </tr>

              {/* Task Management Section */}
              <tr className="bg-gradient-to-r from-orange-100 via-red-100 to-orange-100">
                <td colSpan={4} className="py-4 px-6 font-bold text-gray-800 text-base">
                  🧠 Task Management (Slack Tool)
                </td>
              </tr>
              <tr className="border-b border-gray-100 bg-gray-50">
                <td className="py-3 px-6 text-gray-500 italic text-sm"></td>
                <td className="py-3 px-6 text-center text-amber-600 italic font-medium text-sm">🚀 coming soon</td>
                <td className="py-3 px-6 text-center text-white italic font-medium text-sm bg-gradient-to-br from-[#3667B2] to-[#2d5291]">🚀 coming soon</td>
                <td className="py-3 px-6 text-center text-amber-600 italic font-medium text-sm">🚀 coming soon</td>
              </tr>
              <tr className="border-b border-gray-100 hover:bg-orange-50 transition-colors">
                <td className="py-4 px-6 text-gray-700 font-medium">Break project into tasks</td>
                <td className="py-4 px-6 text-center text-gray-600">1 / task</td>
                <td className="py-4 px-6 text-center text-white bg-gradient-to-br from-[#3667B2] to-[#2d5291] font-semibold">Unlimited</td>
                <td className="py-4 px-6 text-center text-gray-600">Unlimited</td>
              </tr>
              <tr className="hover:bg-orange-50 transition-colors">
                <td className="py-4 px-6 text-gray-700 font-medium">Auto-assign tasks</td>
                <td className="py-4 px-6 text-center text-gray-600">1 / task</td>
                <td className="py-4 px-6 text-center text-white bg-gradient-to-br from-[#3667B2] to-[#2d5291] font-semibold">Unlimited</td>
                <td className="py-4 px-6 text-center text-gray-600">Unlimited</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Payment Modal for Credits */}
      {showPaymentModal && (
        <div
          className="fixed inset-0 bg-gray-800 bg-opacity-50 flex justify-center items-center z-50"
          onClick={() => setShowPaymentModal(false)}
        >
          <div
            className="bg-white w-[95%] max-w-md rounded-2xl shadow-2xl p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowPaymentModal(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-900 text-2xl"
            >
              &times;
            </button>

            <h2 className="text-2xl font-bold text-gray-800 mb-4">Buy Credits</h2>
            <p className="text-gray-600 mb-6">Top up your credits for AI features across all Zynth tools.</p>

            {/* Current Credits */}
            <div className="flex items-center gap-2 mb-4">
              <FaCoins className="text-yellow-500" />
              <span className="text-gray-700">Current Credits: <strong>{creditsAvailable}</strong></span>
            </div>

            {/* Credits Selector */}
            <div className="flex items-center gap-4 mb-6">
              <button
                className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                onClick={() => setCreditsToBuy((prev) => Math.max(10, prev - 10))}
              >
                -
              </button>
              <input
                type="number"
                value={creditsToBuy}
                readOnly
                className="border border-gray-300 rounded-lg px-4 py-2 w-24 text-center"
              />
              <button
                className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                onClick={() => setCreditsToBuy((prev) => prev + 10)}
              >
                +
              </button>
            </div>

            {/* Price */}
            <div className="text-center mb-6">
              <span className="text-3xl font-bold text-gray-900">
                {currencySymbol}{(creditsToBuy * creditValue).toFixed(2)}
              </span>
              <span className="text-gray-500 ml-2">for {creditsToBuy} credits</span>
            </div>

            {/* Buy Button */}
            <button
              onClick={handleCreditPayment}
              className="w-full bg-[#3667B2] text-white py-3 rounded-xl font-semibold hover:bg-[#2d5291] transition-colors"
            >
              Buy {creditsToBuy} Credits
            </button>

            {/* Payment Gateway (hidden) */}
            <PaymentGateway
              ref={paymentGatewayRef}
              productinfo="Credits Purchase"
              onSuccess={handlePaymentSuccess}
              onFailure={() => toast.error('Payment failed')}
              formId={orgId}
              authToken={authToken}
              isDiscounted={false}
              discountedAmount={0}
              originalAmount={creditsToBuy * creditValue}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default Pricing
